import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  useStripe,
  useElements,
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useStripeSetupIntent, useStripePaymentIntent } from '../utils/pay';
import * as api from '../utils/api';
import {
  logPaymentStep,
  logPaymentError,
  log3DSFlow,
  getPaymentSessionId,
  PAYMENT_STEPS,
} from '../utils/paymentLogger';
import constants from '../utils/constants';
import { isDev, isStaging } from '../utils/utils';
// Components
import PRIVACY_POLICY_URL from '../utils/privacyPolicyUrl';
import Box from './shared/atoms/Box';
import Text from './shared/atoms/Text';
import Checkbox from './shared/atoms/Checkbox';
import Link from './shared/atoms/Link';
import PayFormSkeleton from './PayFormSkeleton';
import PaymentRequestButton from './PaymentRequestButton';

const stripePromise = loadStripe(
  isDev() || isStaging()
    ? constants.STRIPE_KEY_TEST
    : constants.STRIPE_KEY_LIVE,
);

const StyledSpan = styled.span`
  color: ${(p) => p.theme.colors.primary.main};
`;

const StyledBox = styled(Box)`
  .elementStripe {
    font-family: LTSaeada, Arial, sans-serif;
    font-size: 1em !important;
    font-weight: 400;
    padding: 12px 16px;
    border: 1px solid #d7d7d7;
    font-size: 16px;
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.25s ease;

    &:hover {
      border-color: #5b5ff5;
    }

    &:focus-within {
      border-color: #5b5ff5;
      box-shadow: 0 0 0 3px rgba(91, 95, 245, 0.1);
    }

    ::placeholder {
      color: #2e414b;
      opacity: 0.5;
      width: 100%;
    }
  }
  .elementStripeError {
    padding: 12px 16px;
    border: 2px solid #ffa726;
    font-size: 16px;
    background-color: #fff3e0;
    border-radius: 12px;
    ::placeholder {
      color: #2e414b;
      opacity: 0.5;
    }
  }
`;

const errorStipe = (error) => {
  switch (error) {
    case 'invalid_number':
    case 'incomplete_number':
      return ['number'];
    case 'invalid_expiry_month':
    case 'expired_card':
      return ['expiry'];
    case 'invalid_cvc':
    case 'incorrect_cvc':
      return ['cvc'];
    case 'card_declined':
    case 'processing_error':
    case 'missing':
    case 'rate_limit':
    case 'invalid_request_error':
    case 'incorrect_number':
      return ['number', 'cvc', 'expiry'];
    default:
      return [];
  }
};

const getErrorMessage = (error) => {
  switch (error.code) {
    case 'invalid_number':
    case 'incorrect_number':
      return 'Numéro invalide. Vérifiez les 16 chiffres.';
    case 'incomplete_number':
      return 'Numéro incomplet. Veuillez saisir tous les chiffres.';
    case 'invalid_expiry_month':
      return "Date d'expiration invalide. Vérifiez le format MM/AA.";
    case 'expired_card':
      return 'Cette carte a expiré. Utilisez une autre carte.';
    case 'invalid_cvc':
    case 'incorrect_cvc':
      return 'Code de sécurité invalide. Vérifiez les 3 chiffres au dos.';
    case 'card_declined':
      return 'Carte refusée. Veuillez utiliser une autre carte.';
    case 'processing_error':
      return 'Erreur de traitement. Veuillez réessayer.';
    default:
      return error.message || 'Une erreur est survenue. Veuillez réessayer.';
  }
};

const PayFormNew = ({
  client: propsClient,
  stripePayment,
  setStripePayment,
  doPay = false,
  setLoading = () => {},
  amount,
  course,
  customAuth,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { expiry, cvc, number, message, state } = stripePayment;
  // const codepromo = useSelector((s) => s.course.codepromo);
  const client = useSelector((s) => {
    const { client } = s;
    if (typeof propsClient !== 'undefined') return propsClient;
    return client;
  });

  // Pre-checked opt-in to save the card for future off-session charges.
  // Drives setup_future_usage on the api side via save_payment_method.
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);

  const [intent, reloadIntent, intentSave] = doPay
    ? useStripePaymentIntent({
        client_id: client.id,
        amount,
        savePaymentMethod,
        ...(course ? { course_id: course.id } : {}),
      })
    : useStripeSetupIntent();

  // Ticket #564 — _doSend tourne sur un useEffect sans verrou : deux passages
  // rapproches a l'etat 'send' le rejouaient. Le verrou est un ref et pas un state
  // pour etre visible immediatement, sans attendre un re-render.
  const inFlight = useRef(false);

  const checkElementsError = (error) => {
    const sessionId = getPaymentSessionId();

    logPaymentError(error, PAYMENT_STEPS.STRIPE_ERROR, {
      client_id: client && client.id,
      error_code: error && error.code,
      error_message: error && error.message,
      session_id: sessionId,
    });

    setLoading(false);
    const WhereErrorComeFrom = errorStipe(error.code);
    const friendlyMessage = getErrorMessage(error);
    setStripePayment({
      cvc: !WhereErrorComeFrom.indexOf('cvc'),
      number: !WhereErrorComeFrom.indexOf('number'),
      expiry: !WhereErrorComeFrom.indexOf('expiry'),
      state: 'error',
      message: friendlyMessage,
    });
  };

  useEffect(() => {
    const { state } = stripePayment;
    if (state === 'send' && !inFlight.current) {
      inFlight.current = true;
      _doSend().finally(() => {
        inFlight.current = false;
      });
    }
  }, [stripePayment]);

  const _doSend = async () => {
    const sessionId = getPaymentSessionId();
    const cardNumber = elements.getElement(CardNumberElement);

    logPaymentStep(PAYMENT_STEPS.STRIPE_CONFIRM_STARTED, {
      client_id: client && client.id,
      is_payment: doPay,
      session_id: sessionId,
    });

    // La case vient de changer et l'intent est en cours de recreation : le
    // confirmer maintenant sauvegarderait la carte contre le choix affiche.
    if (doPay && intentSave !== savePaymentMethod) {
      // Relance la creation : si elle avait echoue (reseau), le client n'est pas
      // bloque jusqu'au rechargement de la page.
      reloadIntent();
      setStripePayment({
        cvc: false,
        number: false,
        expiry: false,
        state: 'error',
        message: t('paybylink.savecard.wait'),
      });
      return;
    }

    setLoading(true);

    try {
      if (doPay) {
        // pay
        logPaymentStep('stripe_confirm_card_payment_started', {
          client_id: client && client.id,
          session_id: sessionId,
        });

        const rep = await stripe.confirmCardPayment(intent, {
          payment_method: {
            card: cardNumber,
            billing_details: {
              name: `${client.prenom} ${client.nom}`,
            },
          },
          save_payment_method: savePaymentMethod,
        });

        // Check if 3DS was required
        if (
          rep.paymentIntent &&
          rep.paymentIntent.status === 'requires_action'
        ) {
          log3DSFlow('challenge_presented', {
            client_id: client && client.id,
            payment_intent_id: rep.paymentIntent && rep.paymentIntent.id,
            session_id: sessionId,
          });
        }

        if (rep.error) {
          logPaymentStep(
            'stripe_confirm_card_payment_error',
            {
              client_id: client && client.id,
              error_code: rep.error && rep.error.code,
              error_message: rep.error && rep.error.message,
              session_id: sessionId,
            },
            'error',
          );
          checkElementsError(rep.error);
        } else {
          logPaymentStep('stripe_confirm_card_payment_success', {
            client_id: client && client.id,
            payment_intent_id: rep.paymentIntent && rep.paymentIntent.id,
            payment_intent_status:
              rep.paymentIntent && rep.paymentIntent.status,
            session_id: sessionId,
          });
          let ret;
          if (course.paiement !== 'ATELIER') {
            ret = await api.post(`/courses/${course.id}/payment`, {
              payment_id: rep.paymentIntent.id,
            });
          } else {
            ret = await api.post(
              `/ateliers/${course.id}/participants/${client.id}/payment`,
              { payment_id: rep.paymentIntent.id },
            );
          }

          if (ret.error_message) {
            logPaymentError(ret.error_message, PAYMENT_STEPS.API_ERROR, {
              client_id: client && client.id,
              course_id: course && course.id,
              payment_intent_id: rep.paymentIntent && rep.paymentIntent.id,
              session_id: sessionId,
            });
            // Ticket #564 — la carte VIENT d'etre debitee (confirmCardPayment a
            // reussi) et seul l'echo API a echoue. L'ancien code appelait ici
            // checkElementsError(rep.error) avec rep.error undefined : errorStipe()
            // dereferencait error.code, levait une TypeError, et le catch general
            // affichait « Veuillez recharger la page et reessayer » a un client
            // qui venait d'etre debite. Recharger refabrique un intent, et l'ecran
            // « deja paye » ne rattrape pas le cas puisque l'enregistrement a
            // justement echoue. On sort donc sur un etat terminal explicite.
            setLoading(false);
            setStripePayment({
              ...stripePayment,
              state: 'encaisse_non_enregistre',
              message: t('paybylink.errors.charged_not_recorded'),
            });
          } else {
            logPaymentStep('payment_completed_successfully', {
              client_id: client && client.id,
              course_id: course && course.id,
              session_id: sessionId,
            });
            setLoading(false);
            setStripePayment({ ...stripePayment, state: 'succes' });
          }
        }
      } else {
        // save card for later (0€ authorization)
        logPaymentStep('stripe_confirm_card_setup_started', {
          client_id: client && client.id,
          session_id: sessionId,
        });

        // FIX-577: client.id vaut `false` par défaut (reducers/client.jsx)
        // quand la session n'est pas authentifiée. Sans cette garde, le 3DS
        // se lançait quand même et réussissait, puis l'enregistrement
        // échouait sur /clients/false/intents avec un « Not found. » brut —
        // le client venait de confirmer sa carte pour rien.
        if (!client.id) {
          checkElementsError({
            code: 'unauthenticated',
            message: 'Votre session a expiré. Veuillez vous reconnecter.',
          });
          return;
        }

        const rep = await stripe.confirmCardSetup(intent, {
          payment_method: {
            card: cardNumber,
            billing_details: {
              name: `${client.prenom} ${client.nom}`,
            },
          },
        });

        // Check if 3DS was required
        if (rep.setupIntent && rep.setupIntent.status === 'requires_action') {
          log3DSFlow('challenge_presented', {
            client_id: client && client.id,
            setup_intent_id: rep.setupIntent && rep.setupIntent.id,
            session_id: sessionId,
          });
        }

        // Log return from 3DS (if it happened)
        if (
          rep.setupIntent &&
          rep.setupIntent.status === 'succeeded' &&
          rep.setupIntent &&
          rep.setupIntent.last_setup_error === null
        ) {
          log3DSFlow('return_to_site', {
            client_id: client && client.id,
            setup_intent_id: rep.setupIntent && rep.setupIntent.id,
            setup_intent_status: rep.setupIntent && rep.setupIntent.status,
            session_id: sessionId,
          });
        }

        if (rep.error) {
          const errorCode = rep.error && rep.error.code;
          const errorMessage = rep.error && rep.error.message;

          logPaymentStep(
            'stripe_confirm_card_setup_error',
            {
              client_id: client && client.id,
              error_code: errorCode,
              error_message: errorMessage,
              session_id: sessionId,
            },
            'error',
          );

          // Détection d'intent expiré (404 ou resource_missing)
          if (
            errorCode === 'resource_missing' ||
            (errorMessage && errorMessage.includes('No such'))
          ) {
            logPaymentStep(
              'setup_intent_expired_detected',
              {
                client_id: client && client.id,
                error_code: errorCode,
                session_id: sessionId,
              },
              'warning',
            );

            // Régénérer automatiquement l'intent
            reloadIntent();

            // Informer l'utilisateur
            checkElementsError({
              code: 'intent_expired',
              message: 'Votre session a expiré. Veuillez réessayer.',
            });
          } else {
            reloadIntent();
            checkElementsError(rep.error);
          }
        } else {
          logPaymentStep('stripe_confirm_card_setup_success', {
            client_id: client && client.id,
            setup_intent_id: rep.setupIntent && rep.setupIntent.id,
            setup_intent_status: rep.setupIntent && rep.setupIntent.status,
            payment_method_id:
              rep.setupIntent && rep.setupIntent.payment_method,
            session_id: sessionId,
          });
          logPaymentStep(PAYMENT_STEPS.PAYMENT_METHOD_SAVE_STARTED, {
            client_id: client && client.id,
            payment_method_id: rep.setupIntent.payment_method,
            session_id: sessionId,
          });

          try {
            const ret = await api.post(
              `/clients/${client.id}/intents`,
              { id: rep.setupIntent.payment_method },
              customAuth,
            );

            if (ret.error_message) {
              logPaymentError(
                ret.error_message,
                PAYMENT_STEPS.PAYMENT_METHOD_SAVE_FAILED,
                {
                  client_id: client && client.id,
                  payment_method_id: rep.setupIntent.payment_method,
                  error: ret.error_message,
                  session_id: sessionId,
                },
              );
              checkElementsError({
                code: 'api_error',
                message: ret.error_message,
              });
              reloadIntent();
            } else {
              logPaymentStep(PAYMENT_STEPS.PAYMENT_METHOD_SAVED, {
                client_id: client && client.id,
                payment_method_id: rep.setupIntent.payment_method,
                session_id: sessionId,
              });

              log3DSFlow('authentication_success', {
                client_id: client && client.id,
                payment_method_id: rep.setupIntent.payment_method,
                session_id: sessionId,
              });

              setStripePayment({
                ...stripePayment,
                state: 'succes',
                payment_method: rep.setupIntent.payment_method,
              });
              setLoading(false);
            }
          } catch (apiError) {
            logPaymentError(
              apiError,
              PAYMENT_STEPS.PAYMENT_METHOD_SAVE_FAILED,
              {
                client_id: client && client.id,
                payment_method_id: rep.setupIntent.payment_method,
                session_id: sessionId,
              },
            );
            checkElementsError({
              code: 'network_error',
              message: 'Erreur réseau. Veuillez réessayer.',
            });
            setLoading(false);
          }
        }
      }
    } catch (error) {
      // Catch any unexpected errors during the entire flow
      logPaymentError(error, PAYMENT_STEPS.UNKNOWN_ERROR, {
        client_id: client && client.id,
        session_id: sessionId,
        error_name: error && error.name,
        error_stack: error && error.stack,
      });

      checkElementsError({
        code: 'unexpected_error',
        message:
          'Une erreur inattendue est survenue. Veuillez recharger la page et réessayer.',
      });
      setLoading(false);
    }
  };

  const setDefaultStripePayment = (e, value) => {
    const { complete } = e;
    setStripePayment({ ...stripePayment, [value]: false, complete });
  };

  const handleWalletSuccess = async (result) => {
    const sessionId = getPaymentSessionId();

    console.log('✅ Wallet result:', result);

    // Extraire le payment_method de setupIntent ou paymentIntent
    const paymentMethodId = result.payment_method || result.id;
    console.log('✅ Payment method ID:', paymentMethodId);

    logPaymentStep('wallet_payment_success', {
      client_id: client && client.id,
      payment_method_id: paymentMethodId,
      wallet_type: result.walletName || 'unknown',
      session_id: sessionId,
    });

    // FIX-577: même garde que _doSend — client.id à `false` enverrait
    // l'enregistrement vers /clients/false/intents.
    if (!client.id) {
      setStripePayment({
        ...stripePayment,
        state: 'error',
        message: 'Votre session a expiré. Veuillez vous reconnecter.',
      });
      return;
    }

    setLoading(true);

    // Sauvegarder le payment method comme le fait le formulaire carte
    const ret = await api.post(
      `/clients/${client.id}/intents`,
      { id: paymentMethodId },
      customAuth,
    );

    if (ret.error_message) {
      logPaymentError(
        ret.error_message,
        PAYMENT_STEPS.PAYMENT_METHOD_SAVE_FAILED,
        {
          client_id: client && client.id,
          payment_method_id: paymentMethodId,
          wallet_type: result.walletName || 'unknown',
          session_id: sessionId,
        },
      );
      setStripePayment({
        ...stripePayment,
        state: 'error',
        message: ret.error_message,
      });
      setLoading(false);
    } else {
      logPaymentStep(PAYMENT_STEPS.PAYMENT_METHOD_SAVED, {
        client_id: client && client.id,
        payment_method_id: paymentMethodId,
        wallet_type: result.walletName || 'unknown',
        session_id: sessionId,
      });
      setStripePayment({
        ...stripePayment,
        state: 'succes',
        payment_method: paymentMethodId,
      });
      setLoading(false);
    }
  };

  const handleWalletError = (error) => {
    const sessionId = getPaymentSessionId();

    console.error('❌ Wallet error:', error);

    logPaymentError(error, 'wallet_payment_error', {
      client_id: client && client.id,
      error_message: error && error.message,
      session_id: sessionId,
    });

    setStripePayment({
      ...stripePayment,
      state: 'error',
      message: error.message,
    });
  };

  return (
    <Box width="100%" m={0}>
      {!intent && <PayFormSkeleton />}
      {intent && (
        <>
          {/* Apple Pay / Google Pay Button */}
          {(!doPay || intentSave === savePaymentMethod) && (
            <PaymentRequestButton
              amount={Math.round((amount || 0) * 100)}
              currency="eur"
              client={client}
              clientSecret={intent}
              onSuccess={handleWalletSuccess}
              onError={handleWalletError}
              isSetupIntent={!doPay}
              label="Roulezjeunesse"
            />
          )}

          <StyledBox m={0} data-testid="card-number-element">
            <Text
              mb={1}
              as="label"
              htmlFor="card-number"
              variant="small"
              style={{
                fontFamily: 'LTSaeada, Arial, sans-serif',
                fontWeight: 500,
                color: '#2e414b',
                fontSize: '0.9rem',
              }}>
              {t('number.card')}
              <StyledSpan aria-label="requis">*</StyledSpan>
            </Text>
            <CardNumberElement
              id="card-number"
              onChange={(e) => setDefaultStripePayment(e, 'number')}
              className={number ? 'elementStripeError' : 'elementStripe'}
              options={{
                'aria-label': t('number.card'),
                'aria-describedby': number
                  ? 'card-number-error security-note'
                  : 'security-note',
                'aria-invalid': number ? 'true' : 'false',
              }}
            />
            {number && (
              <Text
                id="card-number-error"
                mt={1}
                variant="caption"
                fontWeight="500"
                role="alert"
                data-testid="error-card-number-element"
                style={{ color: '#E65100' }}>
                <span aria-hidden="true">⚠️</span> {message}
              </Text>
            )}
            <Box mt={4} flexDirection={{ xs: 'column', lg: 'row' }}>
              <Box
                mr={{ xs: 0, lg: 2 }}
                width="100%"
                data-testid="card-expiry-element">
                <Text
                  as="label"
                  htmlFor="card-expiry"
                  mb={1}
                  variant="small"
                  style={{
                    fontFamily: 'LTSaeada, Arial, sans-serif',
                    fontWeight: 500,
                    color: '#2e414b',
                    fontSize: '0.9rem',
                  }}>
                  {t('expiration')}
                  <StyledSpan aria-label="requis">*</StyledSpan>
                </Text>
                <CardExpiryElement
                  id="card-expiry"
                  onChange={(e) => setDefaultStripePayment(e, 'expiry')}
                  className={expiry ? 'elementStripeError' : 'elementStripe'}
                  options={{
                    'aria-label': t('expiration'),
                    'aria-describedby': expiry
                      ? 'card-expiry-error'
                      : undefined,
                    'aria-invalid': expiry ? 'true' : 'false',
                  }}
                />
                {expiry && (
                  <Text
                    id="card-expiry-error"
                    mt={1}
                    variant="caption"
                    fontWeight="500"
                    role="alert"
                    style={{ color: '#E65100' }}>
                    <span aria-hidden="true">⚠️</span> {message}
                  </Text>
                )}
              </Box>
              <Box
                ml={{ xs: 0, lg: 2 }}
                mt={{ xs: 4, lg: 0 }}
                width="100%"
                data-testid="card-cvc-element">
                <Text
                  as="label"
                  htmlFor="card-cvc"
                  mb={1}
                  variant="small"
                  style={{
                    fontFamily: 'LTSaeada, Arial, sans-serif',
                    fontWeight: 500,
                    color: '#2e414b',
                    fontSize: '0.9rem',
                  }}>
                  {t('security.code')}
                  <StyledSpan aria-label="requis">*</StyledSpan>
                </Text>
                <CardCvcElement
                  id="card-cvc"
                  onChange={(e) => setDefaultStripePayment(e, 'cvc')}
                  className={cvc ? 'elementStripeError' : 'elementStripe'}
                  options={{
                    'aria-label': t('security.code'),
                    'aria-describedby': cvc ? 'card-cvc-error' : undefined,
                    'aria-invalid': cvc ? 'true' : 'false',
                  }}
                />
                {cvc && (
                  <Text
                    id="card-cvc-error"
                    mt={1}
                    variant="caption"
                    fontWeight="500"
                    role="alert"
                    style={{ color: '#E65100' }}>
                    <span aria-hidden="true">⚠️</span> {message}
                  </Text>
                )}
              </Box>
            </Box>
            {message &&
              (state === 'error' || state === 'encaisse_non_enregistre') &&
              !number &&
              !expiry &&
              !cvc && (
                <Text
                  mt={2}
                  variant="caption"
                  fontWeight="500"
                  style={{ color: '#E65100' }}>
                  ⚠️ {message}
                </Text>
              )}
          </StyledBox>
          {doPay && (
            <Box
              mt={4}
              flexDirection="row"
              alignItems="center"
              flexWrap="wrap"
              data-testid="save-payment-method">
              <Checkbox
                id="save-payment-method"
                checked={savePaymentMethod}
                onChange={(e) => setSavePaymentMethod(e.target.checked)}
                aria-label={t('paybylink.savecard.label')}
                style={{ marginRight: 12 }}
              />
              <Text
                mb={0}
                mr={2}
                variant="small"
                as="label"
                htmlFor="save-payment-method">
                {t('paybylink.savecard.label')}
              </Text>
              <Link
                variant="small"
                color="gray.darker"
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer">
                {t('infotextselector.texts.2')}
              </Link>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const ElementedForm = ({ ...props }) => {
  return (
    <Elements stripe={stripePromise}>
      <PayFormNew {...props} />
    </Elements>
  );
};

export default ElementedForm;
